import axios, { AxiosError } from 'axios';

import { datadogRum } from 'root/datadog/datadogRum';

import { LS_INVOICING_API_ROOT } from '../../constants';
import { formatRefId } from '../../utils/referenceHelpers';
import CreditAccountService, { CustomerData } from './CreditAccountService';

export interface InvoiceAllocation {
  invoiceId: string;
  amount: string;
}

export interface InvoicePaymentInstructionPayloadAllocation {
  invoiceId: string;
  amount: string;
}

export interface InvoicePaymentInstructionPayload {
  totalAmount: string;
  currency: string;
  autoAllocate: boolean;
  invoiceAllocations: Array<InvoicePaymentInstructionPayloadAllocation>;
}

export interface InvoicePaymentInstructionResponse {
  id: string;
  transactionRef: string;
  creditAccountRef: string;
  invoiceAllocations: Array<InvoicePaymentInstructionPayloadAllocation>;
}

export interface UpsertInvoicePaymentInstructionParams {
  /** The credit account ID from R-Series */
  creditAccountId: string | number;
  /** The sale/transaction ID */
  saleId: string | number;
  /** The invoice allocations with invoice IDs and amounts */
  invoiceAllocations: Array<InvoiceAllocation>;
  /** Currency for the payment (ISO 4217) */
  currency: string;
  /** Customer data - required if credit account may not exist on Invoicing */
  customer?: CustomerData;
}

export default class InvoicePaymentInstructionService {
  /**
   * Formats credit account and transaction references.
   * Shared helper for both upsert and delete operations.
   *
   * @param creditAccountId The credit account ID
   * @param saleId The sale/transaction ID
   * @returns Formatted credit account and transaction references
   */
  private static formatReferences(
    creditAccountId: string,
    saleId: string
  ): { creditAccountRef: string; transactionRef: string } {
    return {
      creditAccountRef: formatRefId(creditAccountId),
      transactionRef: formatRefId(saleId),
    };
  }

  /**
   * Builds the API URL for the invoice payment instruction endpoint.
   * Shared helper for both upsert and delete operations.
   *
   * @param creditAccountRef The formatted credit account reference
   * @param transactionRef The formatted transaction reference
   * @returns The complete API URL
   */
  private static buildUrl(creditAccountRef: string, transactionRef: string): string {
    return `${LS_INVOICING_API_ROOT}/credit-accounts/${creditAccountRef}/transactions/${transactionRef}/invoice-payment-instruction`;
  }

  /**
   * Logs an error to Datadog.
   * Shared helper for error logging across operations.
   *
   * @param error The error to log
   * @param context Additional context for the error
   * @param message Custom error message
   */
  private static logError(
    error: unknown,
    context: {
      creditAccountRef: string;
      transactionRef: string;
      invoiceAllocations?: Array<InvoicePaymentInstructionPayloadAllocation>;
    },
    message: string
  ): void {
    datadogRum.addError(error, {
      ...context,
      message,
    });
  }

  /**
   * Validates the required parameters for upsert operation.
   *
   * @param creditAccountId The credit account ID
   * @param saleId The sale/transaction ID
   * @param invoiceAllocations The invoice allocations array
   * @param currency The currency for the payment
   * @throws Error if validation fails
   */
  private static validateUpsertParams(
    creditAccountId: string | number,
    saleId: string | number,
    invoiceAllocations: Array<InvoiceAllocation>,
    currency: string
  ): void {
    if (!creditAccountId || creditAccountId === 0 || creditAccountId === '0') {
      throw new Error('creditAccountId is required');
    }

    if (!saleId) {
      throw new Error(`Missing required parameter: saleId=${saleId}`);
    }

    if (!invoiceAllocations || invoiceAllocations.length === 0) {
      throw new Error('invoiceAllocations must contain at least one allocation');
    }

    if (!currency) {
      throw new Error('currency is required');
    }
  }

  /**
   * Builds the payload for the invoice payment instruction API.
   *
   * @param invoiceAllocations The invoice allocations to format
   * @param currency The currency for the payment
   * @returns The formatted payload
   */
  private static buildUpsertPayload(
    invoiceAllocations: Array<InvoiceAllocation>,
    currency: string
  ): InvoicePaymentInstructionPayload {
    const totalAmount = invoiceAllocations
      .reduce((sum, allocation) => sum + parseFloat(allocation.amount), 0)
      .toFixed(2);

    return {
      totalAmount,
      currency,
      autoAllocate: false,
      invoiceAllocations: invoiceAllocations.map((allocation) => ({
        invoiceId: allocation.invoiceId,
        amount: allocation.amount,
      })),
    };
  }

  /**
   * Extracts the error message from an Axios error response.
   *
   * @param error The axios error
   * @returns The error message string, or empty string if not found
   */
  private static getErrorMessage(error: AxiosError): string {
    if (!error.response?.data) {
      return '';
    }

    const responseData = error.response.data as {
      error?: { message?: string };
      message?: string;
    };

    // Check for nested error.message first (e.g., { error: { message: "..." } })
    if (responseData.error?.message) {
      return responseData.error.message;
    }

    // Fall back to top-level message (e.g., { message: "..." })
    return responseData.message || '';
  }

  /**
   * Checks if an error is a 404 credit account not found error.
   *
   * @param error The axios error
   * @returns True if the error is a credit account not found error
   */
  private static isCreditAccountNotFoundError(error: AxiosError): boolean {
    const is404Error = error.response?.status === 404;
    if (!is404Error) {
      return false;
    }

    const errorMessage = this.getErrorMessage(error);
    return errorMessage.toLowerCase().includes('credit account');
  }

  /**
   * Handles 404 credit account not found error by creating the account and retrying.
   *
   * @param url The API URL
   * @param payload The request payload
   * @param creditAccountIdStr The credit account ID string
   * @param customer The customer data
   * @param currency The currency for the credit account
   * @param creditAccountRef The formatted credit account reference
   * @param transactionRef The formatted transaction reference
   * @param invoiceAllocations The invoice allocations for error logging
   * @returns The invoice payment instruction response
   */
  private static async handleCreditAccountNotFoundOnUpsert(
    url: string,
    payload: InvoicePaymentInstructionPayload,
    creditAccountIdStr: string,
    customer: CustomerData,
    currency: string,
    creditAccountRef: string,
    transactionRef: string,
    invoiceAllocations: Array<InvoiceAllocation>
  ): Promise<InvoicePaymentInstructionResponse & { creditAccountId: string }> {
    try {
      await CreditAccountService.createCreditAccount(
        creditAccountIdStr,
        0, // $0 credit limit for invoice payment instruction flow
        customer,
        currency
      );
      // Retry the invoice payment instruction after creating the credit account
      const { data } = await axios.put<InvoicePaymentInstructionResponse>(url, payload);
      return { ...data, creditAccountId: creditAccountIdStr };
    } catch (retryError) {
      this.logError(
        retryError,
        { creditAccountRef, transactionRef, invoiceAllocations },
        `Failed to create invoice payment instruction after creating credit account for transactionRef: ${transactionRef}`
      );
      throw retryError;
    }
  }

  /**
   * Creates or updates an invoice payment instruction for a transaction.
   * If the credit account doesn't exist on Invoicing (404), it will create it and retry.
   *
   * @param params The parameters for upserting an invoice payment instruction
   * @returns The invoice payment instruction response with the credit account ID used
   */
  static async upsertInvoicePaymentInstruction(
    params: UpsertInvoicePaymentInstructionParams
  ): Promise<InvoicePaymentInstructionResponse & { creditAccountId: string }> {
    const { creditAccountId, saleId, invoiceAllocations, customer, currency } = params;

    this.validateUpsertParams(creditAccountId, saleId, invoiceAllocations, currency);

    const creditAccountIdStr = `${creditAccountId}`;
    const saleIdStr = `${saleId}`;
    const { creditAccountRef, transactionRef } = this.formatReferences(
      creditAccountIdStr,
      saleIdStr
    );

    const payload = this.buildUpsertPayload(invoiceAllocations, currency);
    const url = this.buildUrl(creditAccountRef, transactionRef);

    try {
      const { data } = await axios.put<InvoicePaymentInstructionResponse>(url, payload);
      return { ...data, creditAccountId: creditAccountIdStr };
    } catch (error) {
      const axiosError = error as AxiosError;

      if (this.isCreditAccountNotFoundError(axiosError)) {
        if (!customer || !currency) {
          throw new Error(
            'Customer data and currency are required to create credit account on Invoicing when it does not exist'
          );
        }
        return this.handleCreditAccountNotFoundOnUpsert(
          url,
          payload,
          creditAccountIdStr,
          customer,
          currency,
          creditAccountRef,
          transactionRef,
          payload.invoiceAllocations
        );
      }

      this.logError(
        error,
        { creditAccountRef, transactionRef, invoiceAllocations: payload.invoiceAllocations },
        `Failed to create or update invoice payment instruction for transactionRef: ${transactionRef}`
      );
      throw error;
    }
  }

  /**
   * Deletes a transaction and its associated invoice payment instruction from Invoicing.
   * This is idempotent - returns successfully even if the transaction doesn't exist (404).
   *
   * @param creditAccountId The credit account ID from R-Series
   * @param saleId The sale/transaction ID
   */
  static async deleteInvoicePaymentInstruction(
    creditAccountId: string | number,
    saleId: string | number
  ): Promise<void> {
    if (!creditAccountId || creditAccountId === 0 || creditAccountId === '0' || !saleId) {
      return;
    }

    const creditAccountIdStr = `${creditAccountId}`;
    const saleIdStr = `${saleId}`;
    const { creditAccountRef, transactionRef } = this.formatReferences(
      creditAccountIdStr,
      saleIdStr
    );
    const url = this.buildUrl(creditAccountRef, transactionRef);

    try {
      await axios.delete(url);
    } catch (error) {
      const axiosError = error as AxiosError;

      // Idempotent - 404 means already deleted
      if (axiosError.response?.status === 404) {
        return;
      }

      // Idempotent-ish: if the transaction has already been finalized on Invoicing
      // (e.g. completed), the delete will return 409. Treat that as success so
      // orphan cleanup / UI removal doesn't fail on an already-final state.
      if (axiosError.response?.status === 409) {
        const msg = this.getErrorMessage(axiosError).toLowerCase();
        if (
          msg.includes('transaction status') &&
          msg.includes('must be pending') &&
          msg.includes('conflict')
        ) {
          return;
        }
      }

      this.logError(
        error,
        { creditAccountRef, transactionRef },
        `Failed to delete invoice payment instruction for transactionRef: ${transactionRef}`
      );
      throw error;
    }
  }
}
